import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, Tooltip } from '@mantine/core';
import { ChevronRight, Lock } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { useHaptic } from '@utils/haptic';
import { LIGHT_THEME_MODES, type ThemeMode } from '@constants/colors';
import { sidebarSubAnchor } from '@/features/onboarding';
import { BLACK, WHITE, ensureContrast, hexToRgb } from '@utils/badge-contrast';

interface LinkItem {
  label: string;
  link: string;
  icon: React.ReactElement;
  isMinimized: boolean;
  subLinks?: LinkItem[];
  badge?: string;
  /**
   * On a sub-link: draw `badge` beside its label. Off by default, because
   * sub-link badges were never drawn and several carry stale ones.
   */
  showSubBadge?: boolean;
  /** Translation key for the label, resolved against the active language. */
  i18nKey?: string;
  /** Close mobile drawer after navigating */
  onNavigate?: () => void;
  /** Module disabled for this company: dim the row and block navigation. */
  locked?: boolean;
  /** Opens the contact-admin dialog when a locked row is clicked. */
  onLockedClick?: () => void;
  /**
   * Overrides the accordion's own open state. Used by the onboarding film,
   * which renders the real rail and scripts it; undefined keeps the row
   * self-governing, which is every other render.
   */
  forceOpened?: boolean;
  /**
   * Treat this sub-link (by `link`) as the active one instead of deriving it
   * from the route. `''` forces "none". Same caller as `forceOpened`.
   */
  forceActiveSubLink?: string;
}

/** Translate a sidebar badge; falls back to the original text when unknown. */
const BADGE_KEYS: Record<string, string> = {
  BOD: 'badge.bod',
  New: 'badge.new',
  Mulai: 'badge.mulai',
  'AI Ready': 'badge.aiReady',
};

/** Themes whose sidebar surface is light — active pills must use dark text. */
const LIGHT_SIDEBAR_MODES: ThemeMode[] = LIGHT_THEME_MODES;

/** Ink for the current page in the sidebar: blue on light sidebars, a softer blue on dark ones. */
export const SELECT_INK = { light: '#1D5FBF', dark: '#7FB0F5' } as const;

// Badge contrast helpers live in @utils/badge-contrast (shared with the doc-pabean Kolom chip).
/** The light-sidebar chip tint, also used as its pill background. */

/* ------------------------------------------------------------------ *
 *  Motion presets — futuristic micro-interactions
 * ------------------------------------------------------------------ */

/** Staggered entrance for each top-level row (fade + lift + breathe). */
const rowEntrance = (index: number) => ({
  initial: { opacity: 0, x: -14, filter: 'blur(3px)' },
  animate: { opacity: 1, x: 0, filter: 'blur(0px)' },
  transition: {
    delay: 0.06 * index + 0.12,
    duration: 0.38,
    ease: [0.22, 1, 0.36, 1] as const,
  },
});

/** Icon micro-pop on hover / active. */

/** Animated submenu expand / collapse. */
const subMenuMotion = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.26, ease: [0.22, 1, 0.36, 1] as const },
};

/**
 * A sub-link matches its own path and its descendant routes — `/foo` covers
 * `/foo/123` but never `/foobar`. Bare prefix matching is not enough on its
 * own: siblings often nest (`/customer-approval` is the parent path of
 * `/customer-approval/email-groups`), so `activeSubLinkIndex` picks the
 * longest match and only that one lights up.
 */
function isSubLinkActive(pathName: string | undefined, link: string): boolean {
  const basePath = link.split('?')[0];
  const currentBasePath = pathName?.split('?')[0];
  if (!currentBasePath || !basePath || basePath === '#') return false;
  if (basePath === '/') return currentBasePath === '/';
  return currentBasePath === basePath || currentBasePath.startsWith(`${basePath}/`);
}

/** Index of the sub-link owning the current path; -1 when none match. */
function activeSubLinkIndex(pathName: string | undefined, subLinks?: LinkItem[]): number {
  let best = -1;
  let bestLength = -1;
  subLinks?.forEach((subItem, index) => {
    if (!isSubLinkActive(pathName, subItem.link)) return;
    const length = subItem.link.split('?')[0].length;
    if (length > bestLength) {
      bestLength = length;
      best = index;
    }
  });
  return best;
}

export default function LinksGroup(item: LinkItem) {
  const location = useLocation();
  const pathName = location.pathname;
  const [openedState, setOpened] = useState(false);
  const opened = item.forceOpened ?? openedState;
  const { mode, colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const hapticNav = useHaptic('light');
  const hapticToggle = useHaptic('medium');

  const labelOf = (entry: LinkItem) => (entry.i18nKey ? t(entry.i18nKey) : entry.label);
  const badgeOf = (badge?: string) => (badge ? (BADGE_KEYS[badge] ? t(BADGE_KEYS[badge]) : badge) : undefined);

  const activeSubIndex =
    item.forceActiveSubLink !== undefined
      ? item.subLinks?.findIndex((subItem) => subItem.link === item.forceActiveSubLink) ?? -1
      : activeSubLinkIndex(pathName, item.subLinks);

  useEffect(() => {
    if (pathName?.startsWith(item.link) || activeSubLinkIndex(pathName, item.subLinks) >= 0) {
      setOpened(true);
    }
  }, [pathName, item.link, item.subLinks]);

  const hasActiveSubLink = activeSubIndex >= 0;
  const isActive =
    hasActiveSubLink ||
    pathName === item.link ||
    (pathName?.startsWith(`${item.link}/`) && item.link !== '/' && item.link !== '/doc-pabean');

  // dark / navy / etc. need translucent white pills — not #F2F2F2 (was washing out labels)
  const isDarkSidebar = !LIGHT_SIDEBAR_MODES.includes(mode);
  const hoverBg = isDarkSidebar ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 42, 68, 0.05)';
  const textColor = isDarkSidebar ? 'rgba(255, 255, 255, 0.78)' : themeColors.textSecondary;
  const activeTextColor = isDarkSidebar ? '#FFFFFF' : themeColors.text;
  const mutedText = isDarkSidebar ? 'rgba(255, 255, 255, 0.5)' : themeColors.textSecondary;
  const guideColor = isDarkSidebar ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 42, 68, 0.12)';

  // Chip background is a 10% wash over the sidebar surface; resolve it so the
  // label's contrast is measured against what it actually sits on.
  const sidebarSurface = hexToRgb(themeColors.sidebar ?? themeColors.background ?? '#1A221F');
  // The current page: a soft tint of the theme's own accent with accent text, kept readable on this
  // theme's sidebar. An open group is just bold text.
  const selectInk = ensureContrast(
    themeColors.accent ?? themeColors.primary ?? (isDarkSidebar ? SELECT_INK.dark : SELECT_INK.light),
    sidebarSurface,
    isDarkSidebar ? WHITE : BLACK
  );
  const activeBg = `color-mix(in srgb, ${selectInk} 14%, transparent)`;

  const rowBase = 'w-full flex items-center rounded-xl group relative';
  const expandedRow = `${rowBase} gap-2.5 px-3 py-2`;
  const minimizedRow = `${rowBase} justify-center p-2`;

  const handleNavigate = () => {
    hapticNav();
    item.onNavigate?.();
  };

  // The same chip on a row and on the sub-links that opt in (showSubBadge: Master › Barang).
  const badgeChip = (badge?: string) =>
    badge ? (
      <span
        className="ml-auto shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-md uppercase tracking-wide"
        style={{ color: selectInk, backgroundColor: activeBg }}
      >
        {badgeOf(badge)}
      </span>
    ) : null;
  const BadgeChip = badgeChip(item.badge);

  const IconSlot = (
    <span
      className="flex items-center justify-center text-[15px] shrink-0"
      style={{ color: isActive && !item.subLinks ? selectInk : isActive ? activeTextColor : mutedText, width: 18 }}
    >
      {item.icon}
    </span>
  );

  const SubLinksList = (
    <motion.div {...subMenuMotion} style={{ overflow: 'hidden' }} className="ml-4 pl-3">
      <div className="mt-0.5 space-y-0.5" style={{ borderLeft: `1px solid ${guideColor}` }}>
        {item.subLinks?.map((subItem, i) => {
          const isSubActive = i === activeSubIndex;
          return (
            <div key={subItem.link} data-tour={sidebarSubAnchor(item.label, subItem.label)}>
              <Link
                to={subItem.link}
                onClick={handleNavigate}
                className="flex items-center gap-2.5 pl-2.5 pr-2 py-1.5 rounded-lg text-[13px] transition-colors duration-150"
                style={{
                  backgroundColor: isSubActive ? activeBg : 'transparent',
                  color: isSubActive ? selectInk : textColor,
                  fontWeight: isSubActive ? 600 : 400,
                }}
              >
                <span className="text-[13px] shrink-0" style={{ color: isSubActive ? selectInk : mutedText }}>
                  {subItem.icon}
                </span>
                <span className="truncate">{labelOf(subItem)}</span>
                {subItem.showSubBadge && badgeChip(subItem.badge)}
              </Link>
            </div>
          );
        })}
      </div>
    </motion.div>
  );

  // Locked module: one dimmed row for every variant — no flyout, no
  // accordion, no navigation; clicking explains how to activate it.
  if (item.locked) {
    const lockedButton = (
      <motion.button
        type="button"
        {...rowEntrance(0)}
        onClick={() => {
          hapticNav();
          item.onLockedClick?.();
        }}
        className={item.isMinimized ? minimizedRow : expandedRow}
        style={{ border: '1px solid transparent', cursor: 'pointer', opacity: 0.55 }}
        whileTap={{ scale: 0.97 }}
        aria-disabled
      >
        {IconSlot}
        {!item.isMinimized && (
          <>
            <span className="text-[13px] font-medium truncate flex-1 text-left">{labelOf(item)}</span>
            <span className="shrink-0 flex items-center" style={{ color: mutedText }}>
              <Lock size={12} strokeWidth={1.5} />
            </span>
          </>
        )}
      </motion.button>
    );

    if (item.isMinimized) {
      return (
        <Tooltip label={labelOf(item)} position="right" withArrow openDelay={300}>
          {lockedButton}
        </Tooltip>
      );
    }
    return lockedButton;
  }

  // Minimized + subLinks: hover flyout so deep links stay reachable
  if (item.subLinks && item.isMinimized) {
    return (
      <Menu
        trigger="hover"
        position="right-start"
        offset={8}
        transitionProps={{ transition: 'fade', duration: 150 }}
        styles={{
          dropdown: {
            backgroundColor: themeColors.card,
            border: `1px solid ${themeColors.border}`,
            borderRadius: 8,
            padding: 6,
            minWidth: 200,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          },
        }}
      >
        <Menu.Target>
          <Tooltip label={labelOf(item)} position="right" withArrow openDelay={300}>
            <motion.button
              type="button"
              {...rowEntrance(0)}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.backgroundColor = hoverBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isActive ? activeBg : 'transparent';
              }}
              className={minimizedRow}
              style={{ backgroundColor: isActive ? activeBg : 'transparent', border: '1px solid transparent' }}
              whileTap={{ scale: 0.95 }}
              aria-label={labelOf(item)}
            >
              {IconSlot}
              {item.badge && (
                <span
                  className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: themeColors.error }}
                />
              )}
            </motion.button>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <div
            className="px-2.5 py-1.5 mb-1 text-[12px] font-semibold"
            style={{ color: mutedText }}
          >
            {labelOf(item)}
          </div>
          {item.subLinks.map((subItem, i) => {
            const isSubActive = i === activeSubIndex;
            return (
              <Menu.Item
                key={subItem.link}
                component={Link}
                to={subItem.link}
                onClick={handleNavigate}
                leftSection={
                  <motion.span
                    whileTap={{ scale: 0.85 }}
                    style={{ fontSize: 14, color: isSubActive ? selectInk : undefined }}
                  >
                    {subItem.icon}
                  </motion.span>
                }
                style={{
                  backgroundColor: isSubActive ? activeBg : 'transparent',
                  color: isSubActive ? activeTextColor : textColor,
                  fontWeight: isSubActive ? 600 : 400,
                  borderRadius: 6,
                  fontSize: 13,
                }}
                rightSection={subItem.showSubBadge && badgeChip(subItem.badge)}
              >
                {labelOf(subItem)}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
    );
  }

  // Expanded (or mobile): inline accordion for items with subLinks
  if (item.subLinks) {
    return (
      <div style={{ position: 'relative' }}>
        <motion.button
          type="button"
          onClick={() => {
            hapticToggle();
            setOpened(!opened);
          }}
          {...rowEntrance(0)}
          className={item.isMinimized ? minimizedRow : expandedRow}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = hoverBg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          style={{ border: '1px solid transparent', cursor: 'pointer', color: isActive ? activeTextColor : textColor }}
          whileTap={{ scale: 0.985 }}
        >
          {IconSlot}
          {!item.isMinimized && (
            <>
              <span className={`text-[13px] truncate flex-1 text-left ${isActive ? 'font-semibold' : 'font-medium'}`}>
                {labelOf(item)}
              </span>
              {BadgeChip}
              <motion.span
                animate={{ rotate: opened ? 90 : 0 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                className="shrink-0 flex items-center"
                style={{ color: mutedText, display: 'flex' }}
              >
                <ChevronRight size={12} strokeWidth={1.5} />
              </motion.span>
            </>
          )}
        </motion.button>
        {!item.isMinimized && <AnimatePresence initial={false}>{opened && SubLinksList}</AnimatePresence>}
      </div>
    );
  }

  // Leaf link
  const leafButton = (
    <motion.button
      type="button"
      {...rowEntrance(0)}
      className={item.isMinimized ? minimizedRow : expandedRow}
      onMouseEnter={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = hoverBg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isActive ? activeBg : 'transparent';
      }}
      style={{
        backgroundColor: isActive ? activeBg : 'transparent',
        border: '1px solid transparent',
        color: isActive ? selectInk : textColor,
        cursor: 'pointer',
      }}
      whileTap={{ scale: isActive ? 1 : 0.97 }}
    >
      {IconSlot}
      {!item.isMinimized && (
        <>
          <span className="text-[13px] font-medium truncate flex-1 text-left">{labelOf(item)}</span>
          {BadgeChip}
        </>
      )}
    </motion.button>
  );

  if (item.isMinimized) {
    return (
      <Tooltip label={labelOf(item)} position="right" withArrow openDelay={300}>
        <Link to={item.link} className="block" onClick={handleNavigate}>
          {leafButton}
        </Link>
      </Tooltip>
    );
  }

  return (
    <Link to={item.link} className="block" onClick={handleNavigate}>
      {leafButton}
    </Link>
  );
}
